import { ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import {
    getIdentityBaseUrl,
    ProductKey,
    getSwitchAccountUrl,
    fontSize
} from "@/components/signup/utils";
import { useUserContext } from "@/components/user-context";
import { Button } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";

export const SitesList = ({
    sites
}: {
    sites: { displayName: string; url: string }[];
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const {
        profile,
        product,
        user: { email }
    } = useUserContext();
    const orgId = profile.attributes?.managingOrg?.orgId;

    return (
        <div css={containerCss}>
            <p css={switchAccountSubheader}>
                <FormattedMessage
                    id="BxpSignup.SiteExpansionForm.cobrandedSubheader.2"
                    defaultMessage="You’re logged in as {email}. <switchAccountLink>Switch account</switchAccountLink>"
                    values={{
                        email: <strong css={emailCss}>{email}</strong>,
                        switchAccountLink
                    }}
                />
            </p>
            <div css={sitesListCss}>
                {sites.map(({ displayName, url }) => (
                    <a
                        css={siteBoxCss}
                        key={url}
                        href={getIdentityUrl({
                            url,
                            productKey: product?.key as ProductKey
                        })}
                        onClick={(e) => {
                            e.preventDefault(); // wait for event to fire
                            createAnalyticsEvent({
                                eventType: "ui",
                                action: "clicked",
                                actionSubject: "button",
                                actionSubjectId:
                                    "guardClickReturnToProductButton",
                                attributes: {
                                    orgId,
                                    productName: product?.name
                                }
                            }).fire();
                            window.location.href = getIdentityUrl({
                                url,
                                productKey: product?.key as ProductKey
                            });
                        }}
                    >
                        <div css={siteCss}>
                            <p css={productNameCss}>{product?.name}</p>
                            <div css={siteNameCss}>{displayName}</div>
                        </div>
                        <div css={goToSiteButtonContainerCss}>
                            <Button
                                variant="secondary"
                                size="small"
                                color="black"
                            >
                                <FormattedMessage
                                    id="bxpSignup.siteExpansion.goToProduct"
                                    defaultMessage="Go to {productName}"
                                    values={{
                                        productName: product?.description
                                    }}
                                />
                            </Button>
                        </div>
                    </a>
                ))}
            </div>
        </div>
    );
};

const switchAccountLink = (children: ReactNode) => (
    <a href={getSwitchAccountUrl()} css={switchAccountLinkCss}>
        {children}
    </a>
);

const getIdentityUrl = ({
    url,
    productKey
}: {
    url: string;
    productKey: string;
}) => {
    switch (productKey) {
        case ProductKey.JIRA_SOFTWARE:
            return `${getIdentityBaseUrl()}/login?${new URLSearchParams({
                continue: `${url}/jira`,
                application: "jira"
            })}`;
        case ProductKey.CONFLUENCE:
            return `${getIdentityBaseUrl()}/login?${new URLSearchParams({
                continue: `${url}/wiki`,
                application: "confluence"
            })}`;
        case ProductKey.DEVAI:
            return `${getIdentityBaseUrl()}/login?${new URLSearchParams({
                continue: `${url}/dev-agents`,
                application: "devai"
            })}`;
        default:
            return url;
    }
};

const containerCss = css({
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    gap: 16
});

const switchAccountSubheader = css({
    margin: 0,
    fontSize: 14,
    textAlign: "center"
});

const sitesListCss = css({
    alignItems: "center",
    display: "grid",
    width: "100%",
    [media.above.md]: {
        maxHeight: 79.5 * 3.5, // show max 3.5 sites per request from design
        overflowY: "auto"
    }
});

const siteBoxCss = css({
    alignItems: "center",
    borderBlockEnd: `1px solid ${token("color.border")}`,
    display: "grid",
    gap: 4,
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    justifyContent: "space-between",
    padding: "16px 8px",
    width: "100%",
    [":hover"]: {
        textDecoration: "none !important"
    },
    [":last-child"]: {
        borderBlockEnd: "none"
    }
});

const productNameCss = css({
    fontWeight: 400,
    margin: 0
});

const siteCss = css({
    fontSize: fontSize.md
});

const siteNameCss = css({
    color: token("color.text.subtlest"),
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "14px"
});

const goToSiteButtonContainerCss = css({
    maxWidth: 190,
    display: "flex",
    justifyContent: "flex-end"
});

const emailCss = css({
    fontWeight: 600,
    hyphens: "auto",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    wordWrap: "break-word"
});

const switchAccountLinkCss = css({
    textDecoration: "underline",
    color: wacToken("color.primary.blue"),
    whiteSpace: "nowrap",
    [":hover"]: {
        color: wacToken("color.extended.blue800")
    }
});
